import {Directive, ElementRef, HostListener, Input, Optional, Self} from '@angular/core';
import { NgControl } from '@angular/forms';
import {isNil} from 'lodash';

@Directive({
    selector: '[itaxNumberOnly]'
})
export class NumberOnlyDirective {

  // Allow decimal numbers. The \. is only allowed once to occur
  private regex: RegExp = new RegExp(/^-?(?:\d+|\d{1,3}(?:,\d{3})+)(?:(,)\d*)?(?:(\.)\d*)?$/g);

  // Allow key codes for special events. Reflect :
  // Backspace, tab, end, insurance
  private specialKeys: Array<string> = ['Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'Meta'];
  private specialKeyCodes: Array<string> = ['KeyC', 'KeyV', 'KeyA', 'KeyA', 'KeyX'];

  constructor(
    private el: ElementRef,
    @Optional() @Self() private control: NgControl
  ) {
  }

  @Input() maxValue: number;
  @Input() minValue: number;
  @Input() decimal: number;

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Do not use event.keycode this is deprecated.
    // See: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
    const current: string = this.el.nativeElement.value;

    // Allow Backspace, tab, end, and insurance keys
    if (event.key === ',') {
      event.preventDefault();
      return;
    }
    if (this.specialKeys.indexOf(event.key) !== -1 ||
      ((event.ctrlKey || event.metaKey) && this.specialKeyCodes.indexOf(event.code) !== -1)) {
      setTimeout(() => {
        this.el.nativeElement.value = this.cleanToMatchRegex(this.el.nativeElement.value);
        this.handleDecimal(this.el.nativeElement.value, event);
        this.updateFormControlValue(this.el.nativeElement.value);
      }, 100);
      return;
    }

    // We need this because the current value on the DOM element
    // is not yet updated with the value from this event
    const next: string = current.concat(event.key);
    if (next && !String(next).match(this.regex)) {
      event.preventDefault();
      return;
    }
    if (next && (!isNil(this.maxValue) || !isNil(this.minValue))) {
      const numberValue = parseFloat(next);
      if (isNaN(numberValue)
        || (!isNil(this.maxValue) && numberValue > this.maxValue)
        || (!isNil(this.minValue) && numberValue < this.minValue)) {
        event.preventDefault();
        return;
      }
    }

    this.handleDecimal(next, event);
  }

  private handleDecimal(val, event) {
    if (!isNil(this.decimal)) {
      const splitted = val.split('.');
      if (splitted.length === 2) {
        if (splitted[1].length > this.decimal) {
          event.preventDefault();
          return;
        }
        if (splitted[1].length === this.decimal) {
          const regexZero: RegExp = new RegExp(/^(0)+/g);
          if (String(splitted[1]).match(regexZero)) {
            this.el.nativeElement.value = splitted[0];
            event.preventDefault();
            return;
          }
        }
      }
    }
  }

  private cleanToMatchRegex(input) {
    // Step 1: Remove all characters except digits, comma, dot, and minus
    let cleaned = input.replace(/[^\d.,-]/g, '');

    // Step 2: Only allow one leading minus
    cleaned = cleaned.replace(/(?!^)-/g, '');

    return cleaned;
  }

  private updateFormControlValue(val) {
    if (this.control && this.control.control) {
      this.control.control.setValue(val, {
        emitEvent: true,     // Trigger valueChanges
        emitModelToViewChange: true,
        emitViewToModelChange: true
      });
    }
  }
}
